import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    scan,
    shareReplay,
    switchMap,
    take,
    tap
} from 'rxjs/operators';
import {
    AddressResult,
    SearchResult,
    StopRouteResult,
    TransitNetworkPartType,
    PoiAddressResponse,
    PoiAddressResult
} from './search.model';
import { Itinerary, JourneyDirection, OpenTripPlannerFacade, OtpUtil, Route } from '../open-trip-planner';
import Env from '@ridango/core/env';
import { AppStartupService, LatLng, TimeUtil } from '@ridango/core';
import { SearchUtil } from './search.util';
import { TranslateService } from '@ngx-translate/core';
import { Temporal } from '@js-temporal/polyfill';

export interface RoutePoint {
    name: string;
    municipalityName?: string;
    coordinates: LatLng;
    useCurrentLocation: boolean;
    stopId?: number;
}

export interface FullRouteSearchParams {
    destination: RoutePoint;
    origin: RoutePoint;
    whiteListedRoutes?: Route[];
    searchDateAndTime?: { date: string; time: string; arrive: boolean };
    disabled?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SearchService {
    private _recentsModification$: Subject<void> = new Subject<void>();
    public readonly recentsModification$: Observable<void> = this._recentsModification$.asObservable();
    private readonly searchUtil = SearchUtil;
    searchResultsAnnouncement$ = new BehaviorSubject<string>('');
    searchAnnouncementSub$ = new BehaviorSubject(null);

    private readonly _originParams$: BehaviorSubject<{
        key: string;
        value: RoutePoint;
        whiteListedRoutes?: Route[];
    }> = new BehaviorSubject<{ key: string; value: RoutePoint; whiteListedRoutes?: Route[] }>(null);
    public readonly originParams$: Observable<{
        key: string;
        value: RoutePoint;
        whiteListedRoutes?: Route[];
    }> = this._originParams$.asObservable();
    private readonly _destinationParams$: BehaviorSubject<{
        key: string;
        value: RoutePoint;
        whiteListedRoutes?: Route[];
    }> = new BehaviorSubject<{ key: string; value: RoutePoint; whiteListedRoutes?: Route[] }>(null);
    public readonly destinationParams$: Observable<{
        key: string;
        value: RoutePoint;
        whiteListedRoutes?: Route[];
    }> = this._destinationParams$.asObservable();
    private pickedRouteSearchParams: FullRouteSearchParams;
    private paramsKeysEnum = {
        ORIGIN: 'origin',
        DESTINATION: 'destination',
        ITINERARY: 'itinerary',
        SEARCHDATEANDTIME: 'searchDateAndTime',
        DURATION: 'duration',
        PRICE_ZONE_LIST: 'priceZoneList'
    };
    constructor(
        @Inject('ENV') private env: Env,
        private translate: TranslateService,
        private http: HttpClient,
        private appStartUpService: AppStartupService,
        private openTripPlannerFacade: OpenTripPlannerFacade
    ) {
        this.searchAnnouncementSub$
            .pipe(
                debounceTime(1000),
                filter((items) => !!items)
            )
            .subscribe((searchResultItems) => {
                const totalCount = Object.keys(searchResultItems).reduce((count, key) => {
                    const items = searchResultItems[key];
                    return count + (Array.isArray(items) ? items.length : 0);
                }, 0);
                Promise.resolve().then(() => {
                    const message =
                        totalCount === 1
                            ? this.translate.instant('FOUND_X_ITEM', { count: totalCount })
                            : this.translate.instant('FOUND_X_ITEMS', { count: totalCount });
                    this.searchResultsAnnouncement$.next(message);
                });
            });
    }

    searchLoading = new BehaviorSubject<{
        stopRoutes: boolean;
        addresses: boolean;
        poi: boolean;
    }>({
        stopRoutes: false,
        addresses: false,
        poi: false
    });

    public setPickedRouteSearchParams(params: FullRouteSearchParams): void {
        this.pickedRouteSearchParams = params;
    }

    public updatePickedRouteSearchParams(key: keyof FullRouteSearchParams, value): void {
        this.pickedRouteSearchParams = { ...this.pickedRouteSearchParams, [key]: value };
    }

    public getPickedRouteSearchParams(): FullRouteSearchParams {
        return this.pickedRouteSearchParams;
    }

    public getRoutePointSubject(
        key: string
    ): Observable<{ key: string; value: RoutePoint; whiteListedRoutes?: Route[] }> {
        return key === JourneyDirection.Origin ? this.originParams$ : this.destinationParams$;
    }

    public tickSearchPoint(key: string, items: RoutePoint): void {
        if (key === JourneyDirection.Origin) {
            this._originParams$.next({
                key,
                value: items
            });
        }

        if (key === JourneyDirection.Destination) {
            this._destinationParams$.next({
                key,
                value: items
            });
        }
    }

    InitSearch(term: Subject<string>, limit: number = 25, type?: TransitNetworkPartType): Observable<SearchResult> {
        return term.pipe(
            filter((searchTerm) => searchTerm.trim().length > 0),
            debounceTime(400),
            distinctUntilChanged(),
            tap(() => this.searchLoading.next({ stopRoutes: true, addresses: true, poi: true })),
            switchMap((searchTerm) => {
                return this.performSearch(searchTerm, limit, type).pipe(
                    map((result: SearchResult) => this.mapSearchResults(result))
                );
            })
        );
    }

    private mapSearchResults(result: SearchResult): SearchResult {
        if (this.env?.SPLIT_STOP_ROUTES_SEARCH_RESULTS) {
            return {
                ...result,
                stopRoutes: result.stopRoutes.filter(
                    (stopRoute: StopRouteResult) => stopRoute.type !== TransitNetworkPartType.Route
                )
            };
        } else {
            return result;
        }
    }

    public tickRecentsModification(): void {
        this._recentsModification$.next();
    }

    private performSearch(searchTerm: string, limit: number, type?: TransitNetworkPartType): Observable<SearchResult> {
        const searchEndpoints: Observable<Partial<SearchResult>>[] = [];
        let pendingEndpoints = 0;

        const createSearchEndpoint = (searchFunction, resultKey) => {
            pendingEndpoints++;

            return searchFunction(searchTerm, limit, type).pipe(
                catchError((error: Error) => {
                    pendingEndpoints--;
                    this.clearLoading(pendingEndpoints);
                    console.error(error);
                    return of([]);
                }),
                map((result) => ({ [resultKey]: result })),
                tap(() => {
                    pendingEndpoints--;
                    this.clearLoading(pendingEndpoints);
                })
            );
        };

        if (
            this.env.SEARCH_SETTINGS?.SEARCH_PARAMETERS?.includes('STOP_ROUTES') ||
            !this.env.SEARCH_SETTINGS?.SEARCH_PARAMETERS
        ) {
            searchEndpoints.push(createSearchEndpoint(this.searchStopsRoutes.bind(this), 'stopRoutes'));
        }

        if (!this.searchUtil.startsWithNumber(searchTerm, this.env)) {
            const hasAddressesAndPoi = this.env.SEARCH_SETTINGS?.SEARCH_PARAMETERS?.includes('ADDRESSES_AND_POI');

            if (hasAddressesAndPoi) {
                if (!this.env.WMB) {
                    return;
                }
                pendingEndpoints++;

                const poiAndAddressesEndpoint = this.searchPoiAndAddresses(searchTerm, limit).pipe(
                    shareReplay({ bufferSize: 1, refCount: true }),
                    catchError((error: Error) => {
                        console.error(error);
                        return of({ poi: [], addresses: [] });
                    }),
                    tap(() => {
                        pendingEndpoints--;
                        this.clearLoading(pendingEndpoints);
                    })
                ) as Observable<Partial<SearchResult>>;

                searchEndpoints.push(poiAndAddressesEndpoint);
            } else {
                if (this.env?.SEARCH_SETTINGS?.SEARCH_PARAMETERS?.includes('ADDRESSES')) {
                    searchEndpoints.push(createSearchEndpoint(this.searchAddresses.bind(this), 'addresses'));
                }

                if (this.env?.SEARCH_SETTINGS?.SEARCH_PARAMETERS?.includes('POI')) {
                    searchEndpoints.push(createSearchEndpoint(this.searchPoi.bind(this), 'poi'));
                }
            }
        }

        const emptyResult: SearchResult = {
            stopRoutes: [],
            addresses: [],
            poi: []
        };

        return merge(...searchEndpoints).pipe(
            scan<Partial<SearchResult>, SearchResult>((acc, val) => ({ ...acc, ...val }), emptyResult)
        );
    }

    private clearLoading(pendingOperations: number): void {
        if (pendingOperations === 0) {
            this.searchLoading.next({ stopRoutes: false, addresses: false, poi: false });
        }
    }

    private getStopsRoutesSearchUrl(): string {
        if (this.env.WMB?.DONT_USE_FOR?.includes('SEARCH') || !this.env.WMB?.WMB_PUBLIC_API) {
            return `${this.env.CORE_API_URL}/search-v2/${this.env.SEARCH_REGION}`;
        }

        return `${this.env.WMB.WMB_PUBLIC_API}/v1/search`;
    }

    private searchStopsRoutes(
        term: string,
        limit: number,
        type?: TransitNetworkPartType
    ): Observable<StopRouteResult[]> {
        let params = new HttpParams();
        params = params.append('limit', limit);
        params = params.append('query', term.trim());
        if (type) {
            params = params.append('type', type.toString());
        }

        const url = this.getStopsRoutesSearchUrl();

        return this.http.get<StopRouteResult[]>(url, { params }).pipe(
            tap((items) => {
                items.forEach((item) => (item.color = OtpUtil.resolveColor(item.color)));
            })
        );
    }

    private searchAddresses(term: string, limit: number): Observable<AddressResult[]> {
        const trimmedTerm = term.trim();
        let params = new HttpParams();
        params = params.append('maxResults', limit);
        params = params.append('query', trimmedTerm);

        if (this.env.WMB?.DONT_USE_FOR?.includes('SEARCH')) return of([]);
        if (!this.env.WMB) return of([]);

        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/address/search`;

        return this.http.get<AddressResult[]>(url, { params: params }).pipe(
            tap((items) => {
                items.forEach((item) => (item.type = TransitNetworkPartType.Location));
                return items;
            })
        );
    }

    private searchPoiAndAddresses(
        term: string,
        limit: number
    ): Observable<{ poi: PoiAddressResult[]; addresses: PoiAddressResult[] }> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/geocode`;
        const params = new HttpParams().set('address', term).set('limit', limit.toString());

        return this.http.get<PoiAddressResponse>(url, { params }).pipe(
            map((response) => {
                const pois: PoiAddressResult[] = [];
                const addresses: PoiAddressResult[] = [];
                response.data.forEach((item) => {
                    addresses.push({
                        ...item,
                        type: TransitNetworkPartType.Location
                    });

                    if (item.poi_name) {
                        pois.push({
                            ...item,
                            type: TransitNetworkPartType.POI
                        });
                    }
                });
                return { poi: pois, addresses: addresses };
            }),
            catchError((error) => {
                console.error('Error fetching POIs and Addresses:', error);
                return of({ poi: [], addresses: [] });
            })
        );
    }

    private searchPoi(term: string, limit: number): Observable<any[]> {
        const trimmedTerm = term.trim();
        let params = new HttpParams();
        params = params.append('maxResults', limit);
        params = params.append('query', trimmedTerm);
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/poi/search`;
        return this.http.get<AddressResult[]>(url, { params: params }).pipe(
            tap((items) => {
                items.forEach((item) => (item.type = TransitNetworkPartType.POI));
                return items;
            })
        );
    }
    private getTripId(itinerary: Itinerary): string {
        for (let i = 0; i < itinerary.legs.length; i++) {
            if (itinerary.legs[i].tripId) {
                return itinerary.legs[i].tripId;
            }
        }
    }

    getParamsForUrl(itinerary: Itinerary): string {
        let routeSearchParams = this.getPickedRouteSearchParams();

        if (!routeSearchParams) {
            let searchData = null;
            this.openTripPlannerFacade.search$.pipe(take(1)).subscribe((search) => {
                searchData = search;
            });

            if (searchData?.origin && searchData?.destination) {
                routeSearchParams = searchData as FullRouteSearchParams;
                this.setPickedRouteSearchParams(routeSearchParams);
            } else {
                return '';
            }
        }

        const { origin, destination, searchDateAndTime } = routeSearchParams;
        const itineraryParam = this.getTripId(itinerary);
        const itineraryStartDateTime = Temporal.Instant.fromEpochMilliseconds(itinerary.startTime)
            .toZonedDateTimeISO(this.appStartUpService.getTimezone())
            .toString();
        const date = TimeUtil.getDate(itineraryStartDateTime);
        const time = TimeUtil.getTime(itineraryStartDateTime);
        const arrive = searchDateAndTime?.arrive || false;

        const params = new URLSearchParams({
            [this.paramsKeysEnum.ORIGIN]: JSON.stringify(origin),
            [this.paramsKeysEnum.DESTINATION]: JSON.stringify(destination),
            [this.paramsKeysEnum.ITINERARY]: JSON.stringify({ tripId: itineraryParam }),
            [this.paramsKeysEnum.SEARCHDATEANDTIME]: JSON.stringify({ date, time, arrive }),
            [this.paramsKeysEnum.DURATION]: JSON.stringify(itinerary?.duration || 0),
            [this.paramsKeysEnum.PRICE_ZONE_LIST]: JSON.stringify(itinerary?.fare?.priceZoneList || '')
        });

        return params.toString();
    }

    updateSearchResultsAnnouncementMessage(searchResultItems?: SearchResult) {
        this.searchResultsAnnouncement$.next('');
        this.searchAnnouncementSub$.next(searchResultItems);
    }
}
